import React, { useState } from 'react';
import { Calculator, TrendingUp } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const MiniSimulateurPrevoyanceIndependants = () => {
  const [formData, setFormData] = useState({
    age: 35,
    revenuNet: 100000,
    versement3aActuel: 0,
  });

  const [results, setResults] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
    setResults(null);
    setShowEmailForm(false);
  };

  const calculatePrevoyance = () => {
    const { age, revenuNet, versement3aActuel } = formData;

    // Calcul du plafond 3a pour indépendants
    const plafond3a = Math.min(revenuNet * 0.20, 36288);

    // Économie fiscale estimée (taux marginal moyen Neuchâtel : 30-35%)
    const tauxMarginal = revenuNet > 150000 ? 0.36 : revenuNet > 100000 ? 0.32 : 0.28;
    const economieImpots = Math.round(plafond3a * tauxMarginal);

    // Capital à la retraite (projection simple avec 2% de rendement)
    const anneesRestantes = 65 - age;
    const tauxRendement = 0.02;
    const capitalRetraite = Math.round(
      plafond3a * (((1 + tauxRendement) ** anneesRestantes - 1) / tauxRendement)
    );

    // Manque à gagner si on ne verse pas le maximum
    const manqueGagner = Math.round((plafond3a - versement3aActuel) * tauxMarginal);

    setResults({
      plafond3a: Math.round(plafond3a),
      economieImpots,
      capitalRetraite,
      manqueGagner,
      versementOptimal: Math.round(plafond3a),
      anneesRestantes
    });

    // Afficher le formulaire email après le calcul
    setShowEmailForm(true);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !results) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await axios.post(`${API_BASE_URL}/lead-magnet`, {
        email: email,
        source: 'guide_prevoyance_independants',
        guide: 'prevoyance_independants',
        simulation_data: {
          age: formData.age,
          revenuNet: formData.revenuNet,
          versement3aActuel: formData.versement3aActuel,
          plafond3a: results.plafond3a,
          economieImpots: results.economieImpots,
          capitalRetraite: results.capitalRetraite
        }
      });

      if (response.data.success) {
        setSubmitMessage('✅ Parfait ! Consultez votre boîte mail pour recevoir votre rapport personnalisé.');
        setEmail('');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitMessage('❌ Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8" id="simulateur">
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="w-8 h-8 text-purple-400" />
        <h2 className="text-3xl font-bold text-white">Simulateur Prévoyance Indépendants</h2>
      </div>

      <p className="text-gray-300 mb-6">
        Calculez votre plafond 3a, votre économie fiscale et votre capital retraite estimé.
      </p>

      {/* Formulaire */}
      <div className="space-y-6 mb-6">
        <div>
          <label className="block text-gray-300 mb-2 font-semibold">
            Votre âge actuel
          </label>
          <input
            type="range"
            name="age"
            min="25"
            max="64"
            value={formData.age}
            onChange={handleChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-1">
            <span>25 ans</span>
            <span className="text-purple-400 font-bold">{formData.age} ans</span>
            <span>64 ans</span>
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-semibold">
            Revenu net AVS annuel (CHF)
          </label>
          <input
            type="range"
            name="revenuNet"
            min="30000"
            max="300000"
            step="10000"
            value={formData.revenuNet}
            onChange={handleChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-1">
            <span>CHF 30'000</span>
            <span className="text-purple-400 font-bold">CHF {formData.revenuNet.toLocaleString()}</span>
            <span>CHF 300'000</span>
          </div>
        </div>

        <div>
          <label className="block text-gray-300 mb-2 font-semibold">
            Versement 3a actuel par an (CHF)
          </label>
          <input
            type="range"
            name="versement3aActuel"
            min="0"
            max="36288"
            step="1000"
            value={formData.versement3aActuel}
            onChange={handleChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
          <div className="flex justify-between text-sm text-gray-400 mt-1">
            <span>CHF 0</span>
            <span className="text-purple-400 font-bold">CHF {formData.versement3aActuel.toLocaleString()}</span>
            <span>CHF 36'288</span>
          </div>
        </div>

        <button
          onClick={calculatePrevoyance}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 text-lg"
        >
          <TrendingUp className="w-6 h-6" />
          Calculer ma stratégie optimale
        </button>
      </div>

      {/* Résultats */}
      {results && (
        <div className="space-y-4 pt-6 border-t border-white/20">
          <h3 className="text-2xl font-bold text-white mb-4">📊 Vos résultats :</h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl border border-purple-500/30">
              <p className="text-gray-300 text-sm mb-1">Votre plafond 3a maximum</p>
              <p className="text-3xl font-bold text-purple-400">CHF {results.plafond3a.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">20% de votre revenu net AVS</p>
            </div>

            <div className="p-4 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-xl border border-green-500/30">
              <p className="text-gray-300 text-sm mb-1">Économie fiscale annuelle</p>
              <p className="text-3xl font-bold text-green-400">CHF {results.economieImpots.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Si vous versez le maximum</p>
            </div>

            <div className="p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl border border-blue-500/30">
              <p className="text-gray-300 text-sm mb-1">Capital estimé à 65 ans</p>
              <p className="text-3xl font-bold text-blue-400">CHF {results.capitalRetraite.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Avec {results.anneesRestantes} ans de cotisations (rendement 2%)</p>
            </div>

            {results.manqueGagner > 0 && (
              <div className="p-4 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-xl border border-red-500/30">
                <p className="text-gray-300 text-sm mb-1">Économie fiscale perdue</p>
                <p className="text-3xl font-bold text-red-400">CHF {results.manqueGagner.toLocaleString()}</p>
                <p className="text-xs text-gray-400 mt-1">Si vous ne versez pas le maximum</p>
              </div>
            )}
          </div>

          <div className="p-4 bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg mt-4">
            <p className="text-white font-semibold mb-2">💡 Recommandation ATOM :</p>
            <p className="text-gray-300 text-sm">
              Versez CHF {results.versementOptimal.toLocaleString()} par an en 3a pour maximiser votre économie fiscale 
              et constituer un capital retraite solide. Complétez avec une assurance perte de gain et une protection décès/invalidité.
            </p>
          </div>

          {/* Formulaire email */}
          {showEmailForm && !submitMessage && (
            <form onSubmit={handleEmailSubmit} className="mt-6 p-6 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
              <p className="text-white font-semibold mb-3">
                📧 Recevez votre rapport personnalisé par email
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre email"
                  required
                  className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Envoi...' : 'Recevoir'}
                </button>
              </div>
            </form>
          )}

          {submitMessage && (
            <div className={`mt-4 p-4 rounded-lg ${submitMessage.includes('✅') ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
              <p className="text-white text-sm">{submitMessage}</p>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-6 text-center italic">
        Note : Ces calculs sont indicatifs. Pour une analyse précise de votre situation, contactez-nous.
      </p>
    </div>
  );
};

export default MiniSimulateurPrevoyanceIndependants;
